import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getRankedLeaderboard } from '../../services/rankedService';
import { RankedLeaderboard } from '../../types/ranked';
import TierBadge from '../ranked/TierBadge';
import { medalColor } from '../ranked/tierColors';

/**
 * Season ranked leaderboard — the Ranked tab of `/leaderboard`.
 *
 * Ranks come from SQL RANK() computed over the whole season before pagination,
 * so tied students share a position and page two shows true ranks rather than
 * row offsets. Each row links into `/student/ranked/:userId`, the profile
 * route that previously had nothing linking to it.
 */
const RankedLeaderboardTab: React.FC = () => {
  const theme = useTheme();
  const { authState } = useAuth();
  const user = authState.user;

  const [board, setBoard] = useState<RankedLeaderboard | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getRankedLeaderboard(page + 1, rowsPerPage)
      .then(data => {
        setBoard(data);
        setError(null);
      })
      .catch(() => setError('Could not load the leaderboard.'))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      {loading && !board ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : board && board.entries.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            No ranked results yet this season
          </Typography>
          <Typography color="text.secondary">
            Ratings appear once a live quiz has been played.
          </Typography>
        </Paper>
      ) : (
        board && (
          <Paper
            elevation={0}
            sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}
          >
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Rank</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mathlete</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tier</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Rating
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Quizzes
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {board.entries.map(entry => {
                    const isYou = entry.userId === user?.id;
                    const medal = medalColor(entry.rank);

                    return (
                      <TableRow
                        key={entry.userId}
                        hover
                        component={RouterLink}
                        to={`/student/ranked/${entry.userId}`}
                        sx={{
                          textDecoration: 'none',
                          cursor: 'pointer',
                          ...(isYou ? { bgcolor: alpha(theme.palette.primary.main, 0.06) } : {})
                        }}
                      >
                        <TableCell>
                          <Typography
                            fontWeight={700}
                            sx={{
                              fontFamily: 'monospace',
                              fontVariantNumeric: 'tabular-nums',
                              color: medal ?? 'text.secondary'
                            }}
                          >
                            {entry.rank}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={isYou ? 700 : 500}>
                            {entry.displayName}
                            {isYou && (
                              <Typography
                                component="span"
                                sx={{
                                  ml: 1,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  color: 'primary.main'
                                }}
                              >
                                YOU
                              </Typography>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TierBadge
                            tier={entry.tier.name}
                            label={entry.tier.label}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight={700}
                            sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}
                          >
                            {entry.rating}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}
                          >
                            {entry.gamesPlayed}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={board.total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={event => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
        )
      )}
    </Box>
  );
};

export default RankedLeaderboardTab;
